/**
 * Enhanced Device Data
 * Additional device information including compatibility, pricing, and specifications
 */

export interface EnhancedDeviceData {
  deviceId: string;
  compatiblePanels: string[];
  listPrice: number;
  discountTiers: {
    quantity: number;
    discount: number;
  }[];
  leadTime: string;
  warranty: string;
  certifications: string[];
  dataSheetUrl?: string;
  installGuideUrl?: string;
}

// Enhanced data for devices
const enhancedDataMap: Record<string, EnhancedDeviceData> = {
  'smoke_photo_std': {
    deviceId: 'smoke_photo_std',
    compatiblePanels: ['EST3', 'EST4', 'Notifier NFS2-3030', 'Simplex 4100ES', 'Honeywell Silent Knight'],
    listPrice: 85.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 },
      { quantity: 50, discount: 15 },
      { quantity: 100, discount: 20 }
    ],
    leadTime: '1-2 weeks',
    warranty: '5 years',
    certifications: ['UL 268', 'FM', 'CSFM']
  },
  'smoke_ion_std': {
    deviceId: 'smoke_ion_std',
    compatiblePanels: ['EST3', 'EST4', 'Notifier NFS2-3030', 'Simplex 4100ES'],
    listPrice: 75.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 },
      { quantity: 50, discount: 15 }
    ],
    leadTime: '1-2 weeks',
    warranty: '5 years',
    certifications: ['UL 268', 'FM']
  },
  'smoke_multi': {
    deviceId: 'smoke_multi',
    compatiblePanels: ['EST4', 'Notifier NFS2-3030', 'Simplex 4100ES'],
    listPrice: 195.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 12 },
      { quantity: 50, discount: 18 }
    ],
    leadTime: '2-3 weeks',
    warranty: '5 years',
    certifications: ['UL 268', 'UL 2075', 'FM', 'CSFM']
  },
  'heat_fixed': {
    deviceId: 'heat_fixed',
    compatiblePanels: ['EST3', 'EST4', 'Notifier NFS2-3030', 'Simplex 4100ES', 'Honeywell Silent Knight', 'Fire-Lite'],
    listPrice: 45.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 },
      { quantity: 100, discount: 20 }
    ],
    leadTime: '1 week',
    warranty: '10 years',
    certifications: ['UL 521', 'FM']
  },
  'heat_ror': {
    deviceId: 'heat_ror',
    compatiblePanels: ['EST3', 'EST4', 'Notifier NFS2-3030', 'Simplex 4100ES'],
    listPrice: 55.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 }
    ],
    leadTime: '1-2 weeks',
    warranty: '10 years',
    certifications: ['UL 521', 'FM']
  },
  'pull_station_std': {
    deviceId: 'pull_station_std',
    compatiblePanels: ['Universal'],
    listPrice: 65.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 }
    ],
    leadTime: '1 week',
    warranty: '5 years',
    certifications: ['UL 38', 'ADA']
  },
  'horn_strobe_15cd': {
    deviceId: 'horn_strobe_15cd',
    compatiblePanels: ['Universal 24VDC'],
    listPrice: 125.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 },
      { quantity: 50, discount: 15 }
    ],
    leadTime: '1-2 weeks',
    warranty: '5 years',
    certifications: ['UL 464', 'UL 1971', 'ADA']
  },
  'horn_strobe_75cd': {
    deviceId: 'horn_strobe_75cd',
    compatiblePanels: ['Universal 24VDC'],
    listPrice: 145.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 },
      { quantity: 50, discount: 15 }
    ],
    leadTime: '1-2 weeks',
    warranty: '5 years',
    certifications: ['UL 464', 'UL 1971', 'ADA']
  },
  'horn_strobe_110cd': {
    deviceId: 'horn_strobe_110cd',
    compatiblePanels: ['Universal 24VDC'],
    listPrice: 165.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 12 }
    ],
    leadTime: '1-2 weeks',
    warranty: '5 years',
    certifications: ['UL 464', 'UL 1971', 'ADA']
  },
  'monitor_module': {
    deviceId: 'monitor_module',
    compatiblePanels: ['EST3', 'EST4', 'Notifier NFS2-3030', 'Simplex 4100ES'],
    listPrice: 95.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 }
    ],
    leadTime: '1-2 weeks',
    warranty: '5 years',
    certifications: ['UL 864']
  },
  'control_module': {
    deviceId: 'control_module',
    compatiblePanels: ['EST3', 'EST4', 'Notifier NFS2-3030', 'Simplex 4100ES'],
    listPrice: 105.00,
    discountTiers: [
      { quantity: 10, discount: 5 },
      { quantity: 25, discount: 10 }
    ],
    leadTime: '1-2 weeks',
    warranty: '5 years',
    certifications: ['UL 864']
  }
};

/**
 * Get enhanced data for a device
 */
export function getEnhancedDeviceData(deviceId: string): EnhancedDeviceData | undefined {
  return enhancedDataMap[deviceId];
}

/**
 * Get compatible panels for a device
 */
export function getCompatiblePanels(deviceId: string): string[] {
  return enhancedDataMap[deviceId]?.compatiblePanels || [];
}

/**
 * Calculate discount percentage based on quantity
 */
export function getDiscountPercentage(deviceId: string, quantity: number): number {
  const data = enhancedDataMap[deviceId];
  if (!data) return 0;
  
  let discount = 0;
  for (const tier of data.discountTiers) {
    if (quantity >= tier.quantity) {
      discount = tier.discount;
    }
  }
  return discount;
}

/**
 * Calculate price with discount
 */
export function calculatePrice(deviceId: string, quantity: number): number {
  const data = enhancedDataMap[deviceId];
  if (!data) return 0;
  
  const discount = getDiscountPercentage(deviceId, quantity);
  const unitPrice = data.listPrice * (1 - discount / 100);
  return unitPrice * quantity;
}

/**
 * Get all enhanced device data
 */
export function getAllEnhancedData(): EnhancedDeviceData[] {
  return Object.values(enhancedDataMap);
}
